import type { LogoutOptions } from '@auth0/auth0-react';

import userDeviceTokenClient from 'clients/userDeviceToken';
import { postToNative } from 'utils/native-container/bridge';
import { NativeDeviceToken, getNativeDeviceToken } from 'utils/native-container/deviceToken';

/**
 * How long a logout waits for the deregister call.
 *
 * Deregistration has to happen before `auth0Logout`, because that replaces the
 * document and the bearer token goes with it, so there is no later moment to
 * make an authenticated call. But a farmer on a rural connection must not be
 * held on a spinner to sign out, so the wait is bounded and signing out wins.
 *
 * Losing the race leaves the device registered until the next person signs in,
 * at which point the service rebinds the token to them.
 */
const DEREGISTER_TIMEOUT_MS = 3000;

async function deregisterBeforeLeaving(device: NativeDeviceToken): Promise<void> {
  await Promise.race([
    userDeviceTokenClient.deregisterDeviceToken(device),
    new Promise(resolve => {
      setTimeout(resolve, DEREGISTER_TIMEOUT_MS);
    })
  ]);
}

/**
 * Deregister this handset's push token, then tell the container the user is
 * signed out. Shared by every path that ends in nobody being signed in,
 * whether that's an explicit logout or the session simply disappearing
 * (a silent-refresh failure, a revoked session, another tab signing out).
 *
 * Outside the container (`device` is null) this just announces, same as
 * `logoutAnnouncingToNative`'s no-device branch.
 */
export async function deregisterAndAnnounceSignOut(device: NativeDeviceToken | null): Promise<void> {
  if (device) {
    await deregisterBeforeLeaving(device).catch(() => undefined);
  }
  postToNative({ type: 'USER_SIGNED_OUT' });
}

type Auth0Logout = (options?: LogoutOptions) => Promise<void>;

/**
 * Log out of Auth0, telling the mobile container first.
 *
 * `auth0Logout` replaces the document immediately, so nothing after it runs and
 * no component gets another render. `Auth0TokenHandler` announces a sign-out
 * when its user goes away, but on this path it never gets the chance: the page
 * is gone, and the handler remounts after the redirect with no memory of who
 * just left.
 *
 * The container therefore never hears about the logout, and the device stays
 * registered to that account. The next person to pick up the phone keeps
 * receiving the previous user's farm alerts.
 *
 * `USER_IDENTIFIED` does not rescue it. The container treats an identify as an
 * exclusive rebind, which covers an account *switch*, but these paths end in
 * nobody being signed in at all, so no identify ever follows.
 *
 * **Every Auth0 logout must go through here.** Four call sites did the same
 * thing independently and only one of them announced it; a fifth would have
 * missed it too. `__tests__/logout-call-sites.test.ts` fails the build if a new
 * one calls `logout` directly.
 *
 * It also deregisters this handset's push token, for the same reason and in the
 * same window: `auth0Logout` takes the bearer token with it, so this is the
 * last moment an authenticated call can be made. Telling the container is not
 * enough on its own, because the container holds no credentials and cannot make
 * that call itself.
 *
 * A no-op in a normal browser.
 */
export function logoutAnnouncingToNative(
  auth0Logout: Auth0Logout,
  options?: LogoutOptions
): Promise<void> {
  const device = getNativeDeviceToken();

  // Outside the container there is nothing to deregister, and the logout stays
  // synchronous exactly as it was. Only the container pays the wait, and only
  // it has anything to gain from it. Kept as its own branch rather than routed
  // through the async helper below: that would defer `auth0Logout` to a
  // microtask even here, a real behaviour change from the previous
  // synchronous call that a caller could reasonably depend on.
  if (!device) {
    postToNative({ type: 'USER_SIGNED_OUT' });
    return auth0Logout(options);
  }

  /**
   * Deregister BEFORE announcing, which is the opposite of what it looks like
   * it should be.
   *
   * The container reacts to `USER_SIGNED_OUT` by re-locking, and the lock
   * unmounts the WebView — deliberately, so a mislaid phone shows no farm data.
   * Announcing first therefore destroys the very page that is mid-request, and
   * the deregistration dies on the one path it exists for, leaving the handset
   * registered to someone who has left.
   *
   * Both still happen before `auth0Logout`, which replaces the document and
   * takes the bearer token with it. A failure must never strand someone signed
   * in, so the wait is bounded and the sign-out proceeds regardless.
   */
  return deregisterAndAnnounceSignOut(device).then(() => auth0Logout(options));
}
